/**
 * GridManager
 * Coordinates multiple dynamic-product-grid instances on a single page
 * Finds all grids, creates GridInstance for each, and coordinates scroll/history
 */
class GridManager {
  constructor() {
    this.grids = new Map(); // gridId -> GridInstance
    this.scrollHandler = null;
    this.isInitialized = false;
    this.restorationInProgress = false;
    this.restorationOverlay = null;

    // Throttle state for scroll handling
    this.lastScrollTime = 0;
    const cfg = typeof InfiniteScrollConfig !== "undefined" ? InfiniteScrollConfig : {};
    this.scrollThrottleDelay = cfg.SCROLL_THROTTLE_DELAY || 16; // ~60fps, matches ScrollManager

    // Bind methods
    this.handleWindowScroll = this.handleWindowScroll.bind(this);
    this.handleWindowResize = this.handleWindowResize.bind(this);
  }

  /**
   * Initialize GridManager - find and initialize all grids
   */
  async initialize() {
    if (this.isInitialized) {
      return;
    }

    // Find all grid elements
    const gridElements = document.querySelectorAll("[data-infinite-scroll-v2]");
    
    if (gridElements.length === 0) {
      window.dispatchEvent(new CustomEvent('restorationNotNeeded'));

      this.isInitialized = true;
      return;
    }

    // Initialize each grid
    const initPromises = [];
    for (const gridElement of gridElements) {
      const gridId = gridElement.id;
      if (!gridId) {
        continue;
      }

      try {
        const gridInstance = new GridInstance(gridElement);
        this.grids.set(gridId, gridInstance);
        initPromises.push(gridInstance.initialize());
      } catch (error) {
        // Continue with other grids
      }
    }

    // Wait for all grids to initialize
    await Promise.allSettled(initPromises);

    // Setup scroll listener (single listener for all grids)
    this.setupScrollListener();

    // Setup resize listener
    this.setupResizeListener();

    // Check if restoration is needed
    if (HistoryManager.isRestoration()) {
      await this.coordinateRestoration();
    } else {
      // Fresh load - push current page to history
      const wasPushed = HistoryManager.pushCurrentPage();
      // If skipped (same URL on reload), skip pushState to avoid duplicate browser history entry
      HistoryManager.setupHistoryState(!wasPushed);

      // Take an initial snapshot of all grids for history,
      // so restore params are populated even before any scroll.
      this.updateHistoryState();
      
      // Dispatch event for loading overlay (restoration not needed)
      window.dispatchEvent(new CustomEvent('restorationNotNeeded'));
    }

    this.isInitialized = true;
  }

  /**
   * Setup single window scroll listener for all grids
   */
  setupScrollListener() {
    if (this.scrollHandler) {
      window.removeEventListener("scroll", this.scrollHandler);
    }

    this.scrollHandler = this.handleWindowScroll;
    window.addEventListener("scroll", this.scrollHandler, { passive: true });
  }

  /**
   * Handle window scroll - update all visible grids (with throttling)
   */
  handleWindowScroll() {
    if (this.restorationInProgress) return; // Don't process scroll during restoration

    // Throttle scroll handling to prevent excessive operations
    const now = Date.now();
    if (now - this.lastScrollTime < this.scrollThrottleDelay) {
      return;
    }
    this.lastScrollTime = now;

    // Update each grid's scroll position
    for (const [gridId, gridInstance] of this.grids) {
      if (gridInstance.isInitialized && !gridInstance.isRestoring) {
        try {
          gridInstance.handlePageScroll();
        } catch (error) {
          // Error handled silently
        }
      }
    }
  }

  /**
   * Setup window resize listener
   */
  setupResizeListener() {
    window.addEventListener("resize", this.handleWindowResize, { passive: true });
  }

  /**
   * Handle window resize - update all grids
   */
  handleWindowResize() {
    for (const [gridId, gridInstance] of this.grids) {
      if (gridInstance.isInitialized) {
        try {
          gridInstance.handleResize();
        } catch (error) {
          // Error handled silently
        }
      }
    }
  }

  /**
   * Get all grid instances
   * @returns {Map<string, GridInstance>}
   */
  getGrids() {
    return this.grids;
  }

  /**
   * Get grid instance by ID
   * @param {string} gridId
   * @returns {GridInstance|null}
   */
  getGrid(gridId) {
    return this.grids.get(gridId) || null;
  }

  /**
   * Prevent user interaction during restoration
   */
  preventInteraction() {
    if (this.restorationOverlay) return; // Already preventing

    // Create overlay
    this.restorationOverlay = document.createElement("div");
    this.restorationOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 999999;
      pointer-events: all;
      background: transparent;
    `;
    document.body.appendChild(this.restorationOverlay);
    document.body.style.pointerEvents = "none";
    document.body.style.overflow = "hidden";
  }

  /**
   * Restore user interaction after restoration
   */
  restoreInteraction() {
    if (this.restorationOverlay) {
      this.restorationOverlay.remove();
      this.restorationOverlay = null;
    }
    document.body.style.pointerEvents = "";
    document.body.style.overflow = "";
  }

  /**
   * Restore page scroll position with retry and verify grid positions
   * @param {number} targetScrollY - Target page scroll Y position
   * @param {Object} gridStates - Grid states with carouselIndex for verification
   * @param {number} maxRetries - Maximum retry attempts (default: 3)
   * @returns {Promise<boolean>} - Whether restoration succeeded
   */
  async restorePageScrollWithRetry(targetScrollY, gridStates = {}, maxRetries = null) {
    return;
    const cfg = typeof InfiniteScrollConfig !== "undefined" ? InfiniteScrollConfig : {};
    const retryDelay = cfg.RETRY_DELAY || 1000;
    if (maxRetries === null) {
      maxRetries = cfg.SCROLL_RESTORE_RETRY_ATTEMPTS || 3;
    }

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      // Wait for layout to stabilize
      // await new Promise((resolve) => {
      //   requestAnimationFrame(() => {
      //     requestAnimationFrame(() => {
      //       requestAnimationFrame(resolve);
      //     });
      //   });
      // });

      // Attempt page scroll restoration
      window.scrollTo({
        top: targetScrollY,
        behavior: "instant",
      });

      // Wait for scroll to complete
      await new Promise((resolve) => {
            requestAnimationFrame(resolve);
      });

      // Verify page scroll position (allow 5px tolerance)
      const currentScrollY = window.scrollY;
      const scrollTolerance = 5;
      
      if (Math.abs(currentScrollY - targetScrollY) <= scrollTolerance) {
        // Page scroll is correct, now verify grid positions
        let allGridsVerified = true;

        for (const [gridId, gridInstance] of this.grids) {
          const gridState = gridStates[gridId];
          if (gridState && gridInstance.isInitialized) {
            const targetIndex = gridState.carouselIndex;
            if (targetIndex !== null && targetIndex !== undefined) {
              // Verify grid position (without scrolling, just check current position)
              try {
                const verified = gridInstance.verifyScrollPosition(targetIndex);
                if (!verified) {
                  allGridsVerified = false;
                }
              } catch (error) {
                allGridsVerified = false;
              }
            }
          }
        }

        if (allGridsVerified) {
          return true;
        }
      }

      // Wait before retry (exponential backoff)
      if (attempt < maxRetries - 1) {
        const delay = retryDelay * (attempt + 1);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }

    return false;
  }

  /**
   * Coordinate multi-grid restoration
   * @returns {Promise<void>}
   */
  async coordinateRestoration() {
    if (this.restorationInProgress) {
      return;
    }

    this.restorationInProgress = true;
    this.preventInteraction();

    try {
      // Get restoration params
      const restoreParams = HistoryManager.getRestorationParams();
      if (!restoreParams || !restoreParams.grids) {
        this.restorationInProgress = false;
        this.restoreInteraction();
        // Dispatch event for loading overlay (restoration not needed)
        window.dispatchEvent(new CustomEvent('restorationNotNeeded'));
        return;
      }

      // ─────────────────────────────────────────────────────────────
      // PHASE 1: FAST LAYOUT RESTORATION (Synchronous - Immediate)
      // ─────────────────────────────────────────────────────────────
      // Set all grid heights immediately for instant layout
      for (const [gridId, gridInstance] of this.grids) {
        const gridState = restoreParams.grids[gridId];
        if (gridState) {
          gridInstance.isRestoring = true;
          gridInstance.prepareRestorationLayout(gridState);
        }
      }

      // Restore page scroll position immediately (before data processing)
      if (restoreParams.pageScrollY !== undefined && restoreParams.pageScrollY !== null) {
        await new Promise((resolve) => {
          requestAnimationFrame(resolve);
        });
        await new Promise((resolve) => {
          requestAnimationFrame(resolve);
        });
        window.scrollTo({
          top: restoreParams.pageScrollY,
          behavior: "instant",
        });
      }
      this.restoreInteraction();
      // Dispatch event for loading overlay
      window.dispatchEvent(new CustomEvent('restorationComplete'));
      // ─────────────────────────────────────────────────────────────
      // PHASE 2: DATA RESTORATION (Asynchronous - Can be slower)
      // ─────────────────────────────────────────────────────────────
      // Now handle data, containers, and rendering (can take time)
      const restorePromises = [];
      for (const [gridId, gridInstance] of this.grids) {
        const gridState = restoreParams.grids[gridId];
        if (gridState) {
          restorePromises.push(
            gridInstance.performRestoration(gridState).catch((error) => {
              gridInstance.isRestoring = false;
              // Continue with other grids
            })
          );
        } else {
          // Grid exists but no state in history - perform fresh load
          restorePromises.push(
            gridInstance.performFreshLoad().catch((error) => {
              // Error handled silently
            })
          );
        }
      }

      // Wait for all grids to complete data restoration
      await Promise.allSettled(restorePromises);

      // Mark all grids as no longer restoring
      for (const [gridId, gridInstance] of this.grids) {
        gridInstance.isRestoring = false;
      }

      // Verify scroll position and grid positions with retry (after data is loaded)
      if (restoreParams.pageScrollY !== undefined && restoreParams.pageScrollY !== null) {
        const scrollRestored = await this.restorePageScrollWithRetry(
          restoreParams.pageScrollY,
          restoreParams.grids
        );
      }

      // Complete restoration
      HistoryManager.completeRestoration();
    } catch (error) {
      // Error handled silently
    } finally {
      this.restorationInProgress = false;
      
    }
  }

  /**
   * Update history state for all grids
   * Collects full history state including products for self-contained restoration
   */
  updateHistoryState() {
    const gridStates = {};
    let pageScrollY = window.scrollY;

    for (const [gridId, gridInstance] of this.grids) {
      if (gridInstance.isInitialized && !gridInstance.isRestoring) {
        try {
          // Use getHistoryState() which includes products for self-contained snapshots
          const historyState = gridInstance.getHistoryState();
          gridStates[gridId] = historyState;
        } catch (error) {
          // Fallback to defaults on error
          gridStates[gridId] = {
            carouselIndex: 0,
            activeFeed: "collection",
            height: 0,
            loadedCount: 0,
            totalCount: 0,
          };
        }
      } else {
        // Grid not initialized or restoring - use defaults
        gridStates[gridId] = {
          carouselIndex: 0,
          activeFeed: "collection",
          height: 0,
          loadedCount: 0,
          totalCount: 0,
        };
      }
    }

    HistoryManager.updateCurrentState(gridStates, pageScrollY);
  }

  /**
   * Destroy all grids and cleanup
   */
  destroy() {
    if (this.scrollHandler) {
      window.removeEventListener("scroll", this.scrollHandler);
    }
    window.removeEventListener("resize", this.handleWindowResize);

    for (const [gridId, gridInstance] of this.grids) {
      try {
        gridInstance.destroy();
      } catch (error) {
        // Error handled silently
      }
    }

    this.grids.clear();
    this.restoreInteraction();
    this.isInitialized = false;
  }
}

// Initialize on DOM ready
document.addEventListener("DOMContentLoaded", () => {
  window.gridManager = new GridManager();
  window.gridManager.initialize();
});

// Export for module usage
if (typeof module !== "undefined" && module.exports) {
  module.exports = GridManager;
}

